import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BayerLogoDark from '../../assets/logos/bayer-logo-dark.svg?react'
import BayerLogo from '../../assets/logos/bayer-logo.svg?react'
import ScribblerLogo from '../../assets/logos/scribbler-logo.svg?react'
import { useTheme } from '../../hooks'

export const SPLASH_DURATION_MS = 5000
const SPRING_EASING = 'cubic-bezier(.25, 1, .5, 1)'

const useStyles = makeStyles({
  splash: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'opacity 0.8s ease',
    // Pre-promote to compositing layer so opacity animation doesn't trigger
    // layout recompositing of children mid-animation (prevents logo shift)
    willChange: 'opacity',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  splashHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    height: '42px',
  },
  logoScribbler: {
    width: '42px',
    height: '42px',
    flexShrink: 0,
    transform: 'scale(2.4)',
    transition: `transform 0.6s ${SPRING_EASING}`,
    position: 'relative',
    zIndex: 2,
  },
  logoScribblerShrunk: {
    transform: 'scale(1)',
  },
  logoScribblerImage: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
  // Wrapper expands to reveal the text sliding out from behind the S mark
  textWrapper: {
    overflow: 'hidden',
    maxWidth: '0px',
    transition: `max-width 0.8s ${SPRING_EASING}`,
  },
  textWrapperRevealed: {
    maxWidth: '220px',
  },
  text: {
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '-0.5px',
    whiteSpace: 'nowrap',
    paddingLeft: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground1,
    transform: 'translateX(-100%)',
    opacity: 0,
    transition: `transform 0.8s ${SPRING_EASING}, opacity 0.3s ease`,
  },
  textRevealed: {
    transform: 'translateX(0)',
    opacity: 1,
  },
  // Divider + Bayer logo breathe in slowly after the text
  rightWrapper: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    height: '42px',
    maxWidth: '0px',
    opacity: 0,
    transition: `max-width 2s ${SPRING_EASING}, opacity 0.8s 0.2s ease`,
  },
  rightWrapperRevealed: {
    maxWidth: '160px',
    opacity: 1,
  },
  divider: {
    width: '1px',
    height: '28px',
    backgroundColor: tokens.colorNeutralStroke1,
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalXL,
    marginRight: tokens.spacingHorizontalXL,
  },
  companyLogo: {
    width: '38px',
    height: '38px',
    flexShrink: 0,
    alignSelf: 'flex-start',
    // translateY runs on the compositor thread — immune to subpixel flex
    // recalculations during the max-width animation, unlike marginTop
    transform: 'translateY(2px)',
  },
})

const bayerLogoLabel = 'Bayer'

const SplashScreen = () => {
  const classes = useStyles()
  const { isDark } = useTheme()
  const [shrinkScribbler, setShrinkScribbler] = useState(false)
  const [revealText, setRevealText] = useState(false)
  const [revealRight, setRevealRight] = useState(false)
  const [hidden, setHidden] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    const t1 = setTimeout(() => setShrinkScribbler(true), 600)
    const t2 = setTimeout(() => setRevealText(true), 1300)
    const t3 = setTimeout(() => setRevealRight(true), 2100)
    // Start fading after all layout animations finish (max-width ends at 4100ms)
    // 4200 + 800ms fade = 5000ms = exact ProtectedRoute unmount time
    const t4 = setTimeout(() => setHidden(true), 4200)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
      clearTimeout(t4)
    }
  }, [])

  return (
    <div
      className={mergeClasses(classes.splash, hidden && classes.splashHidden)}
      role="status"
      aria-label={t('login.splashAriaLabel')}>
      <div className={classes.logoRow}>
        <div
          className={mergeClasses(
            classes.logoScribbler,
            shrinkScribbler && classes.logoScribblerShrunk
          )}>
          <ScribblerLogo className={classes.logoScribblerImage} aria-hidden="true" />
        </div>
        <div
          className={mergeClasses(classes.textWrapper, revealText && classes.textWrapperRevealed)}>
          <span className={mergeClasses(classes.text, revealText && classes.textRevealed)}>
            Scribbler
          </span>
        </div>
        <div
          className={mergeClasses(
            classes.rightWrapper,
            revealRight && classes.rightWrapperRevealed
          )}>
          <span className={classes.divider} aria-hidden="true" />
          {isDark ? (
            <BayerLogoDark className={classes.companyLogo} aria-label={bayerLogoLabel} />
          ) : (
            <BayerLogo className={classes.companyLogo} aria-label={bayerLogoLabel} />
          )}
        </div>
      </div>
    </div>
  )
}

export { SplashScreen }
